import { useTasksByIds } from "@coworker/apprestructured/src/shared/hooks/tasks/useTasksByIds";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { TaskListingTypes } from "../constants";
import { deleteTaskLinks } from "../services/tasks.service";
import { getTaskFilter, isOngoing } from "../tools";
import { OngoingTaskLinksResponse, TaskLinks } from "../types/taskLink";

export function useTaskLinks(
  taskLinks: TaskLinks,
  taskListingType: TaskListingTypes
) {
  const SLICE_SIZE = 200;

  const taskIds = useMemo(
    () => Array.from(new Set(taskLinks.map((taskLink) => taskLink.taskId))),
    [taskLinks]
  );

  const {
    data: tasks = [],
    isLoading: isLoadingTasks,
    isFetching: isFetchingTasks,
    isSuccess: hasFetchedTasks,
  } = useTasksByIds(taskIds, taskLinks, false);

  useEffect(() => {
    if (!hasFetchedTasks || isLoadingTasks || isFetchingTasks) return;

    const retainTaskLinksDate = dayjs().subtract(12, "hour").toISOString();
    const oldTaskIds = taskLinks
      .filter((tl) => tl.createdAt < retainTaskLinksDate)
      .map((taskLink) => taskLink.taskId);

    const missing = oldTaskIds.filter((taskId) => {
      const task = tasks.find((task) => task.id === taskId);
      return !task || !isOngoing(task);
    });

    if (missing.length > 0) {
      const idsToDelete = missing.slice(0, SLICE_SIZE);
      deleteTaskLinks(idsToDelete);
    }
  }, [tasks, taskLinks, hasFetchedTasks, isLoadingTasks, isFetchingTasks]);

  const tasksFilterFunction = getTaskFilter(taskListingType);

  const filtered = tasks
    ?.filter(tasksFilterFunction)
    .sort((t1, t2) => (t1.updated_at > t2.updated_at ? 1 : -1));

  const ongoingFirst = filtered
    ?.filter(getTaskFilter(TaskListingTypes.Ongoing))
    .concat(filtered.filter(getTaskFilter(TaskListingTypes.NotOngoing)));

  const ongoingLinks = taskLinks
    .filter((tl) => filtered?.find((t) => t.id === tl.taskId))
    .sort((tl1, tl2) => (tl1.createdAt > tl2.createdAt ? 1 : -1));

  return {
    count: ongoingFirst?.length,
    tasks: ongoingFirst,
    taskLinks: ongoingLinks,
  } as OngoingTaskLinksResponse;
}
